import React, { useState, useEffect } from 'react';
import { CheckCircle, Calendar, Eye, Palette, ClipboardList, Phone, Mail, Clock, ChevronDown, ChevronLeft, ChevronRight, Building, Home as HomeIcon, Hotel, Stethoscope, Store, Zap, Thermometer, Volume2, Cpu, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from "sonner";
import { Link } from 'react-router-dom';
import ConsultBand from '../components/ConsultBand';
import IndoorUnitLooks from '../components/IndoorUnitLooks';
import VRVRange from '../components/VRVRange';

export const VRVPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vrvType: '',
    spaceType: '',
    message: ''
  });

  const [openFaq, setOpenFaq] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check window size to determine if we are on Mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Experience Center Gallery Images (Tagged with isWide for mobile optimization)
  const galleryImages = [
    { 
      src: '/Daikin VRV Alpha Experience Center.webp', 
      alt: 'Daikin VRV Alpha Experience Center Wide View',
      isWide: true 
    },
    { 
      src: '/Daikin VRV X Outdoor and Indoor.webp', 
      alt: 'Daikin VRV X Outdoor and Indoor Setup',
      isWide: false // Tall image
    },
    { 
      src: '/Daikin VRV X.webp', 
      alt: 'Daikin VRV X Commercial Unit',
      isWide: false // Tall image
    },
    { 
      src: '/Daikin VRV Home vs Split AC.webp', 
      alt: 'Daikin VRV Home vs Split AC Setup',
      isWide: true 
    },
    { 
      src: '/Daikin VRV Home.webp', 
      alt: 'Daikin VRV Home Display',
      isWide: false // Tall image
    },
    { 
      src: '/Daikin VRV Side Discharge.webp', 
      alt: 'Daikin VRV Side Discharge Setup',
      isWide: false // Tall image
    },
    { 
      src: '/Daikin VRV Remote Controllers.webp', 
      alt: 'Daikin VRV Working Remote Controllers',
      isWide: true 
    }
  ];

  // Dynamically filter images: Show only wide images on mobile, show all on desktop
  const activeGalleryImages = isMobile ? galleryImages.filter(img => img.isWide) : galleryImages;

  // Reset index if resizing causes out-of-bounds
  useEffect(() => {
    if (currentImageIndex >= activeGalleryImages.length) {
      setCurrentImageIndex(0);
    }
  }, [isMobile, activeGalleryImages.length, currentImageIndex]);

  // Auto-advance the carousel every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === activeGalleryImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);
    return () => clearInterval(timer);
  }, [activeGalleryImages.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === activeGalleryImages.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? activeGalleryImages.length - 1 : prev - 1));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Daikin VRV AC Systems Delhi NCR | VRV Home, Alpha, Side Discharge | Citi Cool';
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Explore Daikin VRV systems — VRV Home, VRV Alpha & VRV Side Discharge. Visit our VRV Experience Center. 35+ years, 1500+ projects across Delhi NCR.');

    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.id = 'vrv-faq-schema';
    faqSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is a VRV air conditioning system?", "acceptedAnswer": { "@type": "Answer", "text": "VRV (Variable Refrigerant Volume) is Daikin's patented central AC technology using one outdoor unit for multiple indoor units, providing individual zone control and 50%+ energy savings." }},
        { "@type": "Question", "name": "What is the difference between VRV Home, VRV Alpha, and VRV Side Discharge?", "acceptedAnswer": { "@type": "Answer", "text": "VRV Home is for residential spaces (up to 8 indoor units). VRV Alpha is AI-powered for commercial use (up to 64 units). VRV Side Discharge has a compact outdoor unit for space-constrained buildings." }},
        { "@type": "Question", "name": "How much does a Daikin VRV system cost in Delhi NCR?", "acceptedAnswer": { "@type": "Answer", "text": "VRV Home for a 3BHK starts from ₹3-5 lakhs. Commercial VRV Alpha is custom-quoted. Visit our Experience Center for accurate pricing." }},
        { "@type": "Question", "name": "Can I visit a VRV showroom in Delhi before buying?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — Citi Cool's VRV Experience Center lets you see working systems by appointment." }},
        { "@type": "Question", "name": "Why choose Citi Cool for VRV installation?", "acceptedAnswer": { "@type": "Answer", "text": "35+ years experience, #1 Daikin dealer North India, MD Club Gold Member, 1500+ projects, and a dedicated VRV Experience Center." }}
      ]
    });
    document.head.appendChild(faqSchema);

    return () => {
      document.getElementById('vrv-faq-schema')?.remove();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formUrl = "https://docs.google.com/forms/d/e/1FAIpQLScYHiUKJ4xt9EZhF_CLzHGnrTxV1UFQEh6a78r0WYzl2i5pGg/formResponse";
    const formDataToSend = new FormData();
    formDataToSend.append("entry.663475068", formData.name);
    formDataToSend.append("entry.1351859543", formData.phone);
    formDataToSend.append("entry.266053166", formData.email);
    formDataToSend.append("entry.377423970", `[VRV Enquiry] Type: ${formData.vrvType} | Space: ${formData.spaceType} | ${formData.message}`);

    try {
      await fetch(formUrl, { method: "POST", body: formDataToSend, mode: "no-cors" });
      toast.success("Thank you for your interest in VRV!", { description: "We'll call you within 24 hours to schedule your Experience Center visit." });
      setFormData({ name: '', phone: '', email: '', vrvType: '', spaceType: '', message: '' });
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'vrv_lead', { event_category: 'lead', event_label: formData.vrvType || 'general' });
      }
    } catch (error) {
      toast.error("Something went wrong", { description: "Please call us at +91 77039 00307." });
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const projectCategories = [
    { icon: Building, label: 'Corporate Offices', desc: 'IT parks, co-working, HQs' },
    { icon: HomeIcon, label: 'Residences', desc: 'Apartments, villas, towers' },
    { icon: Hotel, label: 'Hotels & Banquets', desc: 'Hospitality & events' },
    { icon: Stethoscope, label: 'Hospitals', desc: 'Precision climate control' },
    { icon: Store, label: 'Retail & F&B', desc: 'Restaurants, showrooms' },
    { icon: Building, label: 'Industrial', desc: 'Factories, warehouses' },
  ];

  const faqs = [
    { q: 'What is VRV and how is it different from split ACs?', a: 'VRV (Variable Refrigerant Volume) is Daikin\'s patented central AC technology. Unlike split ACs where every room needs its own outdoor unit, VRV uses one outdoor unit for multiple rooms. This means cleaner building exteriors, individual room temperature control, 50%+ lower electricity bills, and whisper-quiet operation.' },
    { q: 'How much does a VRV system cost for my home?', a: 'VRV Home for a 3BHK apartment typically starts from ₹3–5 lakhs depending on indoor unit count, piping, and layout. The best way to get accurate pricing is to visit our Experience Center with your floor plan — we provide transparent, detailed estimates on the spot.' },
    { q: 'Can I visit and experience VRV before buying?', a: 'Absolutely — that\'s what our VRV Experience Center is for. You can see live VRV systems running, compare indoor unit styles, and get a personalised design consultation. Visits are by appointment so you get dedicated attention.' },
    { q: 'Which VRV is right for me — Home, Alpha, or Side Discharge?', a: 'VRV Home is for residential spaces (up to 8 indoor units, single-phase power). VRV Alpha is AI-powered for commercial and premium residential (up to 64 units, IoT enabled). VRV Side Discharge suits buildings with limited outdoor space. Not sure? Our engineers will recommend the right system at your Experience Center visit.' },
    { q: 'Do you serve Noida, Gurgaon, and other NCR areas?', a: 'Yes — we serve all of Delhi NCR including South Delhi, Noida, Greater Noida, Gurgaon (Gurugram), Faridabad, and Ghaziabad. Our Experience Center is accessible from anywhere in NCR. For large commercial projects, we serve PAN India.' },
    { q: 'Why choose Citi Cool over other VRV dealers?', a: '35+ years experience since 1990. #1 ranked Daikin dealer in Delhi Region. MD Club Gold Member. 100+ VRV completed projects. A dedicated VRV Experience Center — the only dealer in the region where you can try before you buy. Plus, our team includes engineers with Big 4 consulting backgrounds for professional project delivery.' }
  ];

  return (
    <div className="pt-16 lg:pt-20">

      {/* ─── HERO: Experience Center as Primary Hook (Compact & High-Converting) ───
          Desktop (Clarity, Oct 2026): at 85% of the screen height the hero
          looked like the whole page and visitors did not scroll. It is now
          only as tall as its content, so the next section shows below it. */}
      <section className="relative flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="/Daikin VRV Experience Center.jpg" 
            alt="Daikin VRV Experience Center"
            fetchpriority="high"
            className="w-full h-full object-cover object-[65%_center] lg:object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/90 via-blue-900/80 to-black/80 lg:bg-gradient-to-r lg:from-blue-900/95 lg:via-blue-900/80 lg:to-black/40"></div>
        </div>

        {/* Tighter top and bottom padding (py-4 and lg:py-12) */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            
            {/* Left: Messaging */}
            <div>
              <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 lg:px-4 lg:py-1.5 text-[10px] lg:text-xs font-semibold text-blue-100 mb-3 lg:mb-4 shadow-lg">
                <ShieldCheck size={14} className="text-blue-300" />
                Authorized Daikin VRV Dealer
              </div>

              {/* Headline reduced to text-2xl on mobile, text-5xl on desktop */}
              <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white leading-tight mb-2 lg:mb-4">
                Don't Buy VRV on Paper.
                <span className="block text-blue-300 mt-1">Experience It First.</span>
              </h1>

              {/* Paragraph text size and margins reduced */}
              <p className="text-xs sm:text-sm lg:text-base text-white/90 leading-snug lg:leading-relaxed mb-4 lg:mb-5 max-w-lg font-medium">
                Visit our VRV Experience Center. See working VRV systems, compare indoor units, get a personalised design consultation — all before you spend a single rupee.
              </p>

              {/* Buttons pulled tighter together */}
              <div className="flex flex-row flex-wrap gap-2 lg:gap-3 mb-5 lg:mb-8">
                <Button 
                  onClick={() => document.getElementById('vrv-contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-xl flex-1 sm:flex-none h-10 lg:h-12 px-2 lg:px-8 text-xs lg:text-sm"
                >
                  <Calendar className="mr-1.5 lg:mr-2" size={16} />
                  Book Visit
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => document.getElementById('vrv-products')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm flex-1 sm:flex-none h-10 lg:h-12 px-2 lg:px-8 text-xs lg:text-sm"
                >
                  Explore Range
                </Button>
              </div>

              {/* Stats row made more compact */}
              <div className="grid grid-cols-3 gap-2 lg:gap-4">
                {[
                  { value: '35+', label: 'Years Exp.' },
                  { value: '100+', label: 'VRV Projects' },
                  { value: '5000+', label: 'HP Installed' },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg py-1.5 lg:py-2 px-1 shadow-inner">
                    <div className="text-base lg:text-2xl font-bold text-white">{s.value}</div>
                    <div className="text-[8px] lg:text-[10px] text-blue-200 font-semibold mt-0.5 uppercase tracking-wider">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Quick form card (Hidden on Mobile) */}
            <div className="hidden lg:block lg:pl-8">
              <Card className="border-none shadow-2xl bg-white/95 backdrop-blur-md rounded-2xl overflow-hidden">
                <div className="bg-blue-600 h-1.5 w-full"></div>
                <CardHeader className="py-4 lg:pt-6 lg:pb-3 border-b border-gray-100 px-5 lg:px-8">
                  <CardTitle className="text-xl lg:text-2xl font-bold text-gray-900">Schedule Visit</CardTitle>
                  <CardDescription className="text-xs lg:text-sm mt-1">We'll confirm your appointment within 24 hrs.</CardDescription>
                </CardHeader>
                <CardContent className="px-5 lg:px-8 py-4 lg:pb-6">
                  <form onSubmit={handleSubmit} className="space-y-3 lg:space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                      <div>
                        <Label htmlFor="hero-name" className="text-xs text-gray-700 font-bold">Full Name</Label>
                        <Input id="hero-name" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" required className="mt-1 h-9 lg:h-10 text-sm focus-visible:ring-blue-600" />
                      </div>
                      <div>
                        <Label htmlFor="hero-phone" className="text-xs text-gray-700 font-bold">Phone Number</Label>
                        <Input id="hero-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" required className="mt-1 h-9 lg:h-10 text-sm focus-visible:ring-blue-600" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="hero-vrv" className="text-xs text-gray-700 font-bold">Interested In</Label>
                      <select
                        id="hero-vrv"
                        name="vrvType"
                        value={formData.vrvType}
                        onChange={handleChange}
                        className="mt-1 flex h-9 lg:h-10 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                      >
                        <option value="">Select VRV System</option>
                        <option value="VRV Home">VRV Home (Residential)</option>
                        <option value="VRV Alpha">VRV Alpha (Commercial)</option>
                        <option value="VRV Side Discharge">VRV Side Discharge</option>
                        <option value="Not Sure">Not Sure — Need Guidance</option>
                      </select>
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 text-sm mt-1 shadow-md hover:shadow-lg transition-all">
                      Book My Visit
                    </Button>
                    <p className="text-[10px] text-gray-500 text-center font-medium pt-1">
                      <Clock className="inline mr-1 mb-0.5" size={10}/> By appointment only &middot; Mon–Sat, 10 AM–6 PM
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </section>

      {/* ─── EXPERIENCE CENTER DETAIL (Mobile Optimized Carousel Flow) ─── */}
      <section className="py-10 lg:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* MOBILE-ONLY HEADLINE: Shows up above the images on small screens */}
          <div className="block lg:hidden mb-6">
            <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">VRV Experience Center</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2 leading-tight">
              See It. Feel It.<br/>Then Decide.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
            
            {/* Left: Messaging (Moved to bottom on mobile using order-2) */}
            <div className="order-2 lg:order-1">
              
              {/* DESKTOP-ONLY HEADLINE: Shows up inside the left column on large screens */}
              <div className="hidden lg:block mb-6">
                <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">VRV Experience Center</span>
                <h2 className="text-5xl font-bold text-gray-900 mt-2 leading-tight">
                  See It. Feel It.<br/>Then Decide.
                </h2>
              </div>

              <p className="text-gray-600 leading-relaxed mb-6 lg:mb-8 text-base lg:text-lg font-medium">
                Most people invest lakhs in air conditioning based on a brochure. At Citi Cool, we believe you should see, feel, and understand VRV before you commit — just like you'd test-drive a car.
              </p>

              <div className="space-y-4 lg:space-y-6">
                {[
                  { icon: Eye, title: 'See Working VRV Systems', desc: 'Live, running units. Feel the cooling quality. Hear the silence.' },
                  { icon: Palette, title: 'Compare Indoor Unit Styles', desc: 'Ceiling cassette, duct type, hi-wall — see how each fits real spaces.' },
                  { icon: ClipboardList, title: 'Get a Custom Design', desc: 'Bring your floor plan. We\'ll design a VRV layout right there.' },
                  { icon: Zap, title: 'Understand Costs Transparently', desc: 'Piping, placement, power requirements — complete cost breakdown.' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3 lg:gap-4 group">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-50 group-hover:bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300 shadow-sm border border-blue-100">
                      <item.icon className="text-blue-600 group-hover:text-white transition-colors duration-300" size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-base lg:text-lg">{item.title}</h4>
                      <p className="text-sm lg:text-base text-gray-600 mt-0.5 lg:mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 lg:mt-10 flex flex-wrap gap-3 lg:gap-4">
                <Button 
                  onClick={() => document.getElementById('vrv-contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 lg:h-12 px-5 lg:px-6 shadow-lg text-sm lg:text-base"
                >
                  <Calendar className="mr-2" size={16} />
                  Schedule Visit
                </Button>
                <a href="tel:+917703900307">
                  <Button variant="outline" className="font-bold h-10 lg:h-12 px-5 lg:px-6 border-gray-300 text-gray-800 text-sm lg:text-base">
                    <Phone className="mr-2" size={16} />
                    Call Us
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: Rotating Image Carousel (Moved to top on mobile using order-1) */}
            <div className="relative group order-1 lg:order-2">
              <div className="absolute inset-0 bg-blue-600 rounded-2xl transform translate-x-3 translate-y-3 lg:translate-x-4 lg:translate-y-4 opacity-10 transition-transform duration-500 group-hover:translate-x-4 group-hover:translate-y-4"></div>
              
              {/* Carousel Container - Using aspect-video on mobile so it is wide and saves space */}
              <div className="relative w-full aspect-video lg:aspect-square rounded-2xl shadow-xl overflow-hidden z-10 border border-gray-200 bg-gray-50">
                
                {/* Images (Uses dynamic activeGalleryImages based on mobile/desktop) */}
                {activeGalleryImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-contain p-2 lg:p-4 transition-opacity duration-1000 ease-in-out ${
                      index === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  />
                ))}

                {/* Navigation Arrows */}
                <button 
                  onClick={prevImage}
                  className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 bg-black/20 hover:bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all opacity-100 lg:opacity-0 group-hover:opacity-100 z-30"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 bg-black/20 hover:bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all opacity-100 lg:opacity-0 group-hover:opacity-100 z-30"
                  aria-label="Next image"
                >
                  <ChevronRight size={20} />
                </button>

                {/* Pagination Dots */}
                <div className="absolute bottom-3 lg:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 lg:gap-2 z-30">
                  {activeGalleryImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-2 rounded-full transition-all duration-300 shadow-sm ${
                        index === currentImageIndex ? 'bg-blue-600 w-4 lg:w-6' : 'bg-gray-300 w-2 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              
            </div>

          </div>
        </div>
      </section>

      {/* ─── VRV PRODUCTS — SIDE BY SIDE ─── */}
      <section id="vrv-products" className="py-12 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">Daikin VRV Range</span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Choose the Right VRV for Your Space</h2>
            <p className="text-gray-600 mt-3 lg:mt-4 max-w-2xl mx-auto text-sm lg:text-lg">Three systems, three purposes. Not sure which one? Visit our Experience Center and we'll help you choose.</p>
          </div>

          <VRVRange />
        </div>
      </section>

      {/* ─── INDOOR UNIT CORNER, for customers: how each unit looks (src/data/indoorUnits.js) ─── */}
      <section id="indoor-units" className="py-12 lg:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">Indoor Unit Corner</span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mt-2">How Each VRV Indoor Unit Looks in Your Room</h2>
            <p className="text-gray-600 mt-3 lg:mt-4 max-w-3xl mx-auto text-sm lg:text-lg">Hidden behind grilles, a flat panel in the ceiling, or on show: choose the look first. Then see the colours, the finish and how it looks when it is switched off, and come and see them for real at our Experience Center.</p>
          </div>
          <IndoorUnitLooks onVisit={() => document.getElementById('vrv-contact')?.scrollIntoView({ behavior: 'smooth' })} />
          <div className="mt-8 lg:mt-10 flex flex-wrap justify-center gap-3 lg:gap-4">
            <Button
              onClick={() => document.getElementById('vrv-contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 lg:h-12 px-5 lg:px-6 shadow-lg text-sm lg:text-base"
            >
              <Calendar className="mr-2" size={16} />
              Book a Visit to See Them
            </Button>
            <Link to="/architects-interior-designers#units"
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white font-bold h-10 lg:h-12 px-5 lg:px-6 text-gray-800 text-sm lg:text-base hover:bg-gray-50">
              Ceiling space and technical details ›
            </Link>
          </div>
        </div>
      </section>

      {/* ─── WHAT IS VRV — SEO CONTENT ─── */}
      <section className="py-12 lg:py-24 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16 items-start">
            
            {/* Text - 3 cols */}
            <div className="lg:col-span-3">
              <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">Understanding VRV Technology</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4 lg:mb-6 leading-tight">
                What is VRV &amp; Why Is It the Smartest Way to Cool?
              </h2>
              <div className="space-y-4 lg:space-y-5 text-gray-600 leading-relaxed text-sm lg:text-lg">
                <p>
                  VRV (Variable Refrigerant Volume) is Daikin's patented central air conditioning technology — first invented in 1982. Unlike conventional split ACs where every room needs its own outdoor unit, a VRV system uses a single outdoor unit connected to multiple indoor units across your entire space.
                </p>
                <p>
                  The result? Cleaner building exteriors with no outdoor unit clutter, individual temperature control in every room, dramatically lower electricity bills (50%+ savings), and whisper-quiet operation. Whether it's a 3BHK apartment or a 10-floor office tower, VRV adapts to your exact needs.
                </p>
                <p>
                  In India's extreme summer heat — where temperatures routinely cross 45°C in Delhi NCR — Daikin VRV systems are engineered to perform reliably even at 55°C ambient temperature (Daikin VRV Alpha catalogue). The latest VRV Alpha series adds AI and IoT intelligence to anticipate cooling needs and optimise energy usage automatically.
                </p>
              </div>
            </div>

            {/* Benefits - 2 cols */}
            <div className="lg:col-span-2 space-y-3 lg:space-y-4 lg:pt-2">
              {[
                { icon: Zap, title: '50%+ Energy Savings', desc: 'Inverter technology adjusts compressor speed to match exact cooling load.' },
                { icon: Thermometer, title: 'Individual Zone Control', desc: 'Set different temperatures in every room from one central system.' },
                { icon: Volume2, title: 'Whisper-Quiet Operation', desc: 'Swing compressor reduces friction and vibration for near-silent cooling.' },
                { icon: Cpu, title: 'AI & IoT Ready (VRV Alpha)', desc: 'Smart diagnostics, remote monitoring, and predictive maintenance.' },
              ].map((b) => (
                <div key={b.title} className="flex gap-3 lg:gap-4 p-4 lg:p-5 bg-white border border-gray-100 shadow-sm hover:shadow-md rounded-xl lg:rounded-2xl transition-all">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-50 rounded-lg lg:rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                    <b.icon className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm lg:text-base">{b.title}</h4>
                    <p className="text-xs lg:text-sm text-gray-500 mt-1 leading-snug">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROJECTS PORTFOLIO ─── */}
      <section className="py-12 lg:py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">Our VRV Portfolio</span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mt-2">100+ Projects. Every Type of Space.</h2>
            <p className="text-gray-600 mt-3 lg:mt-4 max-w-2xl mx-auto text-sm lg:text-lg font-medium">From premium homes to hotels, restaurants to corporate headquarters — we've designed and installed VRV systems across every category.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
            {projectCategories.map((cat) => (
              <Card key={cat.label} className="border-none shadow-sm lg:shadow-md hover:shadow-xl bg-white transition-all duration-300 hover:-translate-y-1 lg:hover:-translate-y-2 text-center rounded-xl lg:rounded-2xl">
                <CardContent className="p-4 lg:p-6">
                  <div className="w-10 h-10 lg:w-14 lg:h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-blue-100">
                    <cat.icon className="text-blue-600 w-5 h-5 lg:w-6 lg:h-6" />
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-blue-700 uppercase tracking-wide leading-tight mb-1">{cat.label}</div>
                  <div className="text-[10px] lg:text-xs text-gray-500 font-medium">{cat.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTACT / BOOKING FORM ─── */}
      <section id="vrv-contact" className="py-12 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            
            {/* Left: Info */}
            <div>
              <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">Get in Touch</span>
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mt-2 mb-4 lg:mb-6 leading-tight">Schedule Your VRV Experience Center Visit</h2>
              <p className="text-gray-600 leading-relaxed mb-8 lg:mb-10 text-sm lg:text-lg font-medium">
                Investing in air conditioning is a big decision. Our Experience Center lets you see real VRV systems in action, understand the installation process, and get accurate pricing — all before you commit.
              </p>

              <div className="space-y-4 lg:space-y-6 bg-gray-50 p-6 lg:p-8 rounded-2xl border border-gray-100">
                <div className="flex gap-4 lg:gap-5 items-center">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <Phone className="text-white w-4 h-4 lg:w-5 lg:h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm lg:text-lg">Call Us</p>
                    <a href="tel:+917703900307" className="text-blue-600 hover:text-blue-800 font-semibold text-sm lg:text-base transition-colors">+91 77039 00307</a>
                  </div>
                </div>
                <div className="flex gap-4 lg:gap-5 items-center">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <Mail className="text-white w-4 h-4 lg:w-5 lg:h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm lg:text-lg">Email</p>
                    <a href="mailto:admin@citicool.in" className="text-blue-600 hover:text-blue-800 font-semibold text-sm lg:text-base transition-colors">admin@citicool.in</a>
                  </div>
                </div>
                <div className="flex gap-4 lg:gap-5 items-center">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <Clock className="text-white w-4 h-4 lg:w-5 lg:h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm lg:text-lg">By Appointment Only</p>
                    <p className="text-sm lg:text-base font-medium text-gray-600">Monday – Saturday, 10 AM – 6 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <Card className="border-none shadow-xl lg:shadow-2xl bg-white rounded-2xl lg:rounded-3xl overflow-hidden mt-2 lg:mt-0">
              <div className="bg-blue-600 h-1.5 lg:h-2 w-full"></div>
              <CardHeader className="pt-6 lg:pt-8 pb-4 border-b border-gray-50 px-5 lg:px-8">
                <CardTitle className="text-xl lg:text-3xl font-bold">Book Your Consultation</CardTitle>
                <CardDescription className="text-xs lg:text-base font-medium mt-1 lg:mt-2">Fill in your details and we'll reach out within 24 hours.</CardDescription>
              </CardHeader>
              <CardContent className="px-5 lg:px-8 pt-5 lg:pt-6 pb-6 lg:pb-8">
                <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
                    <div>
                      <Label htmlFor="form-name" className="text-xs lg:text-sm text-gray-700 font-bold">Full Name</Label>
                      <Input id="form-name" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" required className="mt-1.5 lg:mt-2 h-10 lg:h-12 text-sm focus-visible:ring-blue-600" />
                    </div>
                    <div>
                      <Label htmlFor="form-phone" className="text-xs lg:text-sm text-gray-700 font-bold">Phone Number</Label>
                      <Input id="form-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" required className="mt-1.5 lg:mt-2 h-10 lg:h-12 text-sm focus-visible:ring-blue-600" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="form-email" className="text-xs lg:text-sm text-gray-700 font-bold">Email (Optional)</Label>
                    <Input id="form-email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@email.com" className="mt-1.5 lg:mt-2 h-10 lg:h-12 text-sm focus-visible:ring-blue-600" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
                    <div>
                      <Label htmlFor="form-vrv" className="text-xs lg:text-sm text-gray-700 font-bold">Interested In</Label>
                      <select id="form-vrv" name="vrvType" value={formData.vrvType} onChange={handleChange} className="mt-1.5 lg:mt-2 flex h-10 lg:h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 font-medium text-gray-700">
                        <option value="">Select VRV System</option>
                        <option value="VRV Home">VRV Home</option>
                        <option value="VRV Alpha">VRV Alpha</option>
                        <option value="VRV Side Discharge">VRV Side Discharge</option>
                        <option value="Not Sure">Not Sure — Need Guidance</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="form-space" className="text-xs lg:text-sm text-gray-700 font-bold">Type of Space</Label>
                      <select id="form-space" name="spaceType" value={formData.spaceType} onChange={handleChange} className="mt-1.5 lg:mt-2 flex h-10 lg:h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 font-medium text-gray-700">
                        <option value="">Select</option>
                        <option value="Apartment">Apartment / Flat</option>
                        <option value="Villa">Villa / Independent House</option>
                        <option value="Office">Office / Corporate</option>
                        <option value="Retail">Retail / Restaurant</option>
                        <option value="Hospital">Hospital / Healthcare</option>
                        <option value="Hotel">Hotel / Banquet</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="form-message" className="text-xs lg:text-sm text-gray-700 font-bold">Anything else?</Label>
                    <Textarea id="form-message" name="message" value={formData.message} onChange={handleChange} placeholder="Approximate area, number of rooms, preferred visit date..." className="mt-1.5 lg:mt-2 min-h-[80px] lg:min-h-[100px] text-sm focus-visible:ring-blue-600 resize-none" />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 lg:h-14 text-base lg:text-lg shadow-lg hover:shadow-xl transition-all" size="lg">
                    Submit & Book My Visit
                  </Button>
                  <p className="text-[10px] lg:text-xs text-gray-500 text-center font-medium">
                    No spam. No obligation. By submitting, you agree to our{' '}
                    <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ─── Paid consultation ─── */}
      <section className="py-10 lg:py-16 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ConsultBand from="vrv" />
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-12 lg:py-24 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <span className="text-blue-600 font-bold text-sm uppercase tracking-wide">FAQ</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-3 lg:space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl lg:rounded-2xl bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 lg:px-6 py-4 lg:py-5 text-left font-bold text-gray-900 hover:bg-gray-50 transition-colors focus:outline-none"
                >
                  <span className="pr-4 text-sm lg:text-lg">{faq.q}</span>
                  <ChevronDown className={`text-blue-600 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} size={20} />
                </button>
                <div 
                  className={`px-5 lg:px-6 text-sm lg:text-base text-gray-600 font-medium leading-relaxed border-t border-gray-100 transition-all duration-300 ease-in-out overflow-hidden ${
                    openFaq === i ? 'max-h-96 py-4 lg:py-5 opacity-100' : 'max-h-0 py-0 opacity-0 border-transparent'
                  }`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BOTTOM CTA STRIP ─── */}
      <section className="bg-gradient-to-r from-blue-700 to-indigo-800 py-12 lg:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 lg:mb-4">Ready to Experience VRV?</h2>
          <p className="text-blue-100 mb-6 lg:mb-8 text-sm lg:text-lg font-medium">Book your appointment at our Experience Center. See it, feel it, then decide.</p>
          <div className="flex flex-wrap justify-center gap-3 lg:gap-4">
            <Button
              onClick={() => document.getElementById('vrv-contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-6 lg:px-8 h-12 lg:h-14 text-sm lg:text-base shadow-xl w-full sm:w-auto"
            >
              <Calendar className="mr-2" size={18} />
              Book Center Visit
            </Button>
            <a href="https://wa.me/message/X4OR7NTM36W5M1" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-6 lg:px-8 h-12 lg:h-14 text-sm lg:text-base font-bold backdrop-blur-sm w-full sm:w-auto">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};